/* GFI Nexus — Google Drive integration
   ─────────────────────────────────────────────────────────────────
   OAuth: Google Identity Services (GIS) token client. Browser-only
   flow — no client_secret involved. The user authorizes once and we
   silently refresh tokens on expiry for as long as they stay signed
   in to Google in this browser.

   Scope: full `drive` so the app can navigate to user-created
   folders (the legacy workflow already creates "ground fresh/incoming
   shipments" via Drive desktop client; we shouldn't force a re-org).

   API: Drive v3 over plain fetch — keeps the bundle small (no gapi
   client lib) and lets us throw real errors on non-2xx responses.

   Token persistence: { access_token, expires_at } in localStorage
   under `gfi-google-drive-token`. We silently re-request when within
   60 s of expiry; if the user has revoked or the session ended,
   `requestAccessToken({ prompt: '' })` resolves to a token without
   UI as long as Google still has consent on file. */

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
/* Two scopes the app uses today: full Drive (navigate to existing
   folders + upload PDFs) and gmail.send (compose-and-send emails on
   behalf of the user). Adding the gmail scope means anyone who's
   already signed in gets prompted to re-consent the first time the
   app needs Gmail — that's expected. */
const SCOPE = "https://www.googleapis.com/auth/drive https://www.googleapis.com/auth/gmail.send";
const TOKEN_KEY = "gfi-google-drive-token";
const FOLDER_CACHE_KEY = "gfi-google-drive-folder-cache";
const FOLDER_MIME = "application/vnd.google-apps.folder";
const DRIVE_API = "https://www.googleapis.com/drive/v3";
const UPLOAD_API = "https://www.googleapis.com/upload/drive/v3/files";

/* ── Token state ──────────────────────────────────────────────── */

let _tokenClient = null;
let _gisReadyPromise = null;

function loadToken() {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    const t = JSON.parse(raw);
    if (!t || !t.access_token || !t.expires_at) return null;
    return t;
  } catch (e) { return null; }
}
function saveToken(t) {
  try { localStorage.setItem(TOKEN_KEY, JSON.stringify(t)); } catch (e) {}
}
function clearToken() {
  try { localStorage.removeItem(TOKEN_KEY); } catch (e) {}
}

export function isConfigured() {
  return !!CLIENT_ID;
}
export function isSignedIn() {
  const t = loadToken();
  if (!t) return false;
  return t.expires_at - Date.now() > 0;
}

/* ── GIS loader ───────────────────────────────────────────────── */

function waitForGIS() {
  if (_gisReadyPromise) return _gisReadyPromise;
  _gisReadyPromise = new Promise(function (resolve, reject) {
    if (window.google && window.google.accounts && window.google.accounts.oauth2) {
      resolve(window.google);
      return;
    }
    /* The <script src="https://accounts.google.com/gsi/client" async defer>
       in index.html may still be loading. Poll for ~10 s. */
    let waited = 0;
    const id = setInterval(function () {
      if (window.google && window.google.accounts && window.google.accounts.oauth2) {
        clearInterval(id);
        resolve(window.google);
        return;
      }
      waited += 100;
      if (waited >= 10000) {
        clearInterval(id);
        reject(new Error("Google Identity Services script did not load"));
      }
    }, 100);
  });
  return _gisReadyPromise;
}

async function getTokenClient() {
  if (_tokenClient) return _tokenClient;
  if (!CLIENT_ID) throw new Error("REACT_APP_GOOGLE_CLIENT_ID not set in .env.local");
  const google = await waitForGIS();
  _tokenClient = google.accounts.oauth2.initTokenClient({
    client_id: CLIENT_ID,
    scope: SCOPE,
    /* Callback set per-request via requestToken(). */
    callback: function () {},
  });
  return _tokenClient;
}

/* Wrap requestAccessToken in a promise. `interactive=false` uses
   `prompt: ''` which Google honors silently when consent is on file;
   `interactive=true` shows the consent / account chooser. */
function requestToken(interactive) {
  return new Promise(async function (resolve, reject) {
    try {
      const client = await getTokenClient();
      client.callback = function (resp) {
        if (resp.error) { reject(new Error(resp.error_description || resp.error)); return; }
        const expires_at = Date.now() + ((resp.expires_in || 3600) - 60) * 1000;
        const t = { access_token: resp.access_token, expires_at: expires_at, scope: resp.scope };
        saveToken(t);
        resolve(t);
      };
      client.requestAccessToken({ prompt: interactive ? "consent" : "" });
    } catch (e) { reject(e); }
  });
}

/* Public sign-in: always shows the account chooser. Use this for the
   first authorisation or after a revocation. */
export async function signIn() {
  return requestToken(true);
}
/* Public sign-out: just drops our token. The user can still revoke
   from their Google account page if they want full removal. */
export function signOut() {
  clearToken();
}

/* Returns a fresh, valid token — refreshing silently if needed.
   Throws if we'd need an interactive prompt (caller should then
   call signIn()). */
async function getValidToken() {
  const t = loadToken();
  if (t && t.expires_at - Date.now() > 0) return t;
  /* Try silent refresh. If the user has revoked or never consented,
     this rejects and the caller must show signIn(). */
  return requestToken(false);
}

/* Public accessor — other modules (gmailApi) need the bearer token to
   call Google APIs beyond Drive. Returns just the access_token string
   to keep the surface area small. */
export async function getAccessToken() {
  const t = await getValidToken();
  return t.access_token;
}

/* ── Folder ID cache ─────────────────────────────────────────── */

/* In-process cache of path → folder ID so we don't re-query Drive on
   every save. Persisted to localStorage so the cache survives
   reloads. Keyed by the full path string we constructed. */
function loadFolderCache() {
  try { return JSON.parse(localStorage.getItem(FOLDER_CACHE_KEY) || "{}") || {}; }
  catch (e) { return {}; }
}
function saveFolderCache(cache) {
  try { localStorage.setItem(FOLDER_CACHE_KEY, JSON.stringify(cache)); } catch (e) {}
}
function cacheGet(pathKey) {
  const c = loadFolderCache();
  return c[pathKey] || null;
}
function cachePut(pathKey, folderId) {
  const c = loadFolderCache();
  c[pathKey] = folderId;
  saveFolderCache(c);
}

/* ── Drive API ────────────────────────────────────────────────── */

async function driveFetch(url, opts) {
  const t = await getValidToken();
  const headers = Object.assign({}, opts && opts.headers, {
    Authorization: "Bearer " + t.access_token,
  });
  const res = await fetch(url, Object.assign({}, opts, { headers: headers }));
  if (!res.ok) {
    let detail = "";
    try { const j = await res.json(); detail = j.error && j.error.message ? j.error.message : JSON.stringify(j); }
    catch (e) { detail = await res.text().catch(function () { return ""; }); }
    throw new Error("Drive API " + res.status + ": " + detail);
  }
  /* 204 No Content (delete) → return null. */
  if (res.status === 204) return null;
  return res.json();
}

/* Escape a string for use in a Drive `name = '...'` query. */
function escapeForQuery(s) {
  return String(s || "").replace(/\\/g, "\\\\").replace(/'/g, "\\'");
}

/* Find a folder by name inside a parent (or root if parentId omitted).
   Returns the first match's ID or null. */
export async function findFolder(name, parentId) {
  const parent = parentId || "root";
  const q = [
    "name = '" + escapeForQuery(name) + "'",
    "mimeType = '" + FOLDER_MIME + "'",
    "trashed = false",
    "'" + parent + "' in parents",
  ].join(" and ");
  const url = DRIVE_API + "/files?q=" + encodeURIComponent(q) + "&fields=files(id,name)&pageSize=10";
  const data = await driveFetch(url);
  return data.files && data.files.length > 0 ? data.files[0].id : null;
}

/* Create a folder by name inside a parent (or root if parentId
   omitted). Returns the new folder's ID. */
export async function createFolder(name, parentId) {
  const body = {
    name: name,
    mimeType: FOLDER_MIME,
    parents: parentId ? [parentId] : ["root"],
  };
  const data = await driveFetch(DRIVE_API + "/files?fields=id,name", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return data.id;
}

/* Find-or-create. Idempotent: if the folder already exists, returns
   its existing ID instead of duplicating. */
export async function ensureFolder(name, parentId) {
  const existing = await findFolder(name, parentId);
  if (existing) return existing;
  return createFolder(name, parentId);
}

/* Walk a path of folder names (e.g. ["ground fresh","incoming
   shipments","season 2025-2026"]) creating each level as needed.
   Returns the leaf folder's ID. Results are cached under the joined
   path so repeated calls skip the lookups. */
export async function ensurePath(segments) {
  if (!Array.isArray(segments) || segments.length === 0) return "root";
  const pathKey = segments.join("/");
  const cached = cacheGet(pathKey);
  if (cached) {
    /* Trust the cache — Drive folders rarely vanish under us. If a
       later API call 404s on this id, the caller can call
       resetFolderCache() to force a re-walk. */
    return cached;
  }
  let parentId = null; /* null = "root" in find/createFolder */
  let runningKey = "";
  for (const seg of segments) {
    runningKey = runningKey ? runningKey + "/" + seg : seg;
    const partial = cacheGet(runningKey);
    if (partial) { parentId = partial; continue; }
    parentId = await ensureFolder(seg, parentId);
    cachePut(runningKey, parentId);
  }
  return parentId;
}

export function resetFolderCache() {
  try { localStorage.removeItem(FOLDER_CACHE_KEY); } catch (e) {}
}

/* ── Upload (used by Phase 2 — kept here so the helper is complete) ─ */

/* Upload a Blob into a parent folder. `name` becomes the file name on
   Drive. Returns { id, name, webViewLink }. Overwrites if a file with
   the same name already exists in the parent — keeps the folder clean
   when Arturo re-runs Download. */
export async function uploadFile(parentId, name, blob, opts) {
  opts = opts || {};
  const t = await getValidToken();
  /* Check for existing file with same name → update instead of insert
     (Drive otherwise creates duplicate filenames in the same folder). */
  const existing = await findFileInFolder(name, parentId);
  const metadata = {
    name: name,
    parents: existing ? undefined : [parentId],
    mimeType: opts.contentType || blob.type || "application/octet-stream",
  };
  /* Multipart body: metadata + content in one PUT/POST. */
  const boundary = "gfi-boundary-" + Date.now();
  const metaPart = "--" + boundary + "\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n" + JSON.stringify(metadata) + "\r\n";
  const dataHeader = "--" + boundary + "\r\nContent-Type: " + metadata.mimeType + "\r\n\r\n";
  const closer = "\r\n--" + boundary + "--";
  const body = new Blob([metaPart, dataHeader, blob, closer], { type: "multipart/related; boundary=" + boundary });
  const url = existing
    ? UPLOAD_API + "/" + existing + "?uploadType=multipart&fields=id,name,webViewLink"
    : UPLOAD_API + "?uploadType=multipart&fields=id,name,webViewLink";
  const res = await fetch(url, {
    method: existing ? "PATCH" : "POST",
    headers: { Authorization: "Bearer " + t.access_token, "Content-Type": body.type },
    body: body,
  });
  if (!res.ok) {
    let detail = ""; try { const j = await res.json(); detail = j.error && j.error.message ? j.error.message : JSON.stringify(j); } catch (e) {}
    throw new Error("Drive upload " + res.status + ": " + detail);
  }
  return res.json();
}

async function findFileInFolder(name, parentId) {
  const q = [
    "name = '" + escapeForQuery(name) + "'",
    "trashed = false",
    "'" + (parentId || "root") + "' in parents",
  ].join(" and ");
  const url = DRIVE_API + "/files?q=" + encodeURIComponent(q) + "&fields=files(id,name)&pageSize=5";
  const data = await driveFetch(url);
  return data.files && data.files.length > 0 ? data.files[0].id : null;
}

/* Returns the URL the user can open the folder at. */
export function folderViewUrl(folderId) {
  return "https://drive.google.com/drive/folders/" + folderId;
}

/* ── GFI naming conventions ──────────────────────────────────── */

/* Split a configured root like "ground fresh/incoming shipments" into
   path segments. Tolerates leading/trailing slashes and "My Drive/"
   prefixes the user might paste from the desktop client. */
export function rootPathSegments(rootPath) {
  const raw = String(rootPath || "ground fresh/incoming shipments").trim();
  return raw
    .replace(/^My Drive[/\\]/i, "")
    .replace(/^[/\\]+|[/\\]+$/g, "")
    .split(/[/\\]+/)
    .filter(Boolean);
}

/* Season label "YYYY-YYYY". `season` is the season record (has
   seasonName / startDate / endEstimate per the app schema). Falls
   back to "season unknown" so callers don't crash on shipments
   without a season FK. */
export function seasonFolderName(season) {
  if (!season) return "season unknown";
  if (season.seasonName) return "season " + season.seasonName;
  if (season.startDate && season.endEstimate) {
    return "season " + season.startDate.substring(0, 4) + "-" + season.endEstimate.substring(0, 4);
  }
  return "season unknown";
}

/* "26-04-29 embarque 58014 Gomez Berries" — uses ARRIVAL date per
   spec. Date format YY-MM-DD. Fallbacks keep the folder name stable
   even if a shipment is missing fields. */
export function embarqueFolderName(shipment, shipperShortName) {
  const dateRaw = shipment && shipment.dateArrival ? shipment.dateArrival : (shipment && shipment.dateDeparture ? shipment.dateDeparture : "");
  const yy = dateRaw ? dateRaw.substring(2, 4) : "??";
  const mm = dateRaw ? dateRaw.substring(5, 7) : "??";
  const dd = dateRaw ? dateRaw.substring(8, 10) : "??";
  const datePart = yy + "-" + mm + "-" + dd;
  const lot = (shipment && shipment.lot) || "00000";
  const sn = shipperShortName || (shipment && shipment.shipperName) || "Unknown";
  return datePart + " embarque " + lot + " " + sn;
}

/* "[PU#] [Client Short Name]" for the invoice subfolder. */
export function invoiceFolderName(invoice, clientShortName) {
  const pu = (invoice && invoice.puNumber) || "no-PU";
  const cn = clientShortName || (invoice && invoice.clientName) || "Unknown";
  return pu + " " + cn;
}

/* ── Open local folder (Windows Explorer via Drive desktop client) ── */

/* Open a local Windows folder from a browser tab. Browsers refuse to
   navigate from http:// to file:// (security), so the reliable path
   is to download an Internet Shortcut (.url) that the user
   double-clicks — Windows Shell resolves file:// URLs by launching
   Explorer to the path. We do all three of:
     1. Best-effort `window.open(file:///...)` (works on Edge with the
        right policy; silent fail elsewhere).
     2. Trigger a .url download named after the folder (the
        guaranteed-to-work path).
     3. Copy the path to the clipboard so the user can also paste it
        into Explorer's address bar (Win+E).
   Returns true unless the .url download couldn't be generated. */
export async function openLocalFolder(absolutePath) {
  if (!absolutePath) return false;
  const winPath = String(absolutePath).replace(/\//g, "\\");
  const fileUrl = "file:///" + winPath.replace(/\\/g, "/");

  /* (1) Direct file:// open — silently fails in Chrome/Firefox. */
  try { window.open(fileUrl, "_blank"); } catch (e) {}

  /* (2) Clipboard copy — best-effort, ignores failure. */
  try { await navigator.clipboard.writeText(winPath); } catch (e) {}

  /* (3) Download a .url Internet Shortcut. Plain-text format, not
     flagged as malicious by browsers (unlike .bat), and Windows
     Shell handles double-click by opening the file:// URL in
     Explorer. */
  try {
    const lastSegment = winPath.split("\\").filter(Boolean).pop() || "folder";
    const safeName = lastSegment.replace(/[^a-zA-Z0-9 _.-]/g, "_").replace(/\s+/g, "_");
    const filename = "open_" + safeName + ".url";
    const body = "[InternetShortcut]\r\nURL=" + fileUrl + "\r\n";
    const blob = new Blob([body], { type: "application/x-mswinurl" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(function () { URL.revokeObjectURL(url); }, 2000);
    return true;
  } catch (e) {
    return false;
  }
}

/* Build a Windows-style local path from the configured local base +
   path segments. Used by the Open Folder buttons in Shipments /
   Invoices so the legacy Drive desktop client mount stays in sync. */
export function buildLocalPath(localBase, segments) {
  const base = String(localBase || "").replace(/[/\\]+$/, "");
  const cleaned = (segments || [])
    .filter(Boolean)
    .map(function (s) { return String(s).replace(/[/\\]+/g, " "); });
  return [base].concat(cleaned).join("\\");
}
